import { Card, Box } from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";
import { EllipsisSX } from "theme";
import { P } from "theme/typography";
import { FieldValueGroupReference } from "../../generated/graphql";
import { FormFieldContext } from "contexts/FormFieldContext";
import { CaseFormContext } from "contexts/CaseFormContext";
import { FormFieldValueGroupUI } from "Backend";
import { ConfirmationDialogContext } from "contexts/ConfirmationDialogContext";
import { EntryContext } from "components/tags/types";
import { FieldValueGroupReferenceLine } from "./FieldValueGroupReferenceLine";

interface FieldValueGroupReferencesProps {
  canDelete?: boolean;
  onClick: (context: EntryContext) => void;
  showDocName?: boolean;
  valueGroup: FormFieldValueGroupUI;
  activeReferenceIndex?: number;
  dataPendo?: string;
}

export const FieldValueGroupReferences = ({
  canDelete,
  onClick,
  showDocName = true,
  valueGroup,
  dataPendo,
}: FieldValueGroupReferencesProps) => {
  const cfContext = useContext(CaseFormContext);
  const ffContext = useContext(FormFieldContext);
  const confirmCtx = useContext(ConfirmationDialogContext);
  const [activeBlockIndex] = cfContext.intakeViewerBlockIndexState;
  const [activeReferenceId] = cfContext.focusFieldReferenceState;
  const pageRangeState = useState<[number, number] | undefined>(undefined);
  const referenceTypeState = useState<"page" | "page_range">("page");
  const referencesMap: {
    [documentName: string]: {
      [page: number]: FieldValueGroupReference[];
    };
  } = useMemo(() => {
    return valueGroup.references.reduce((refMap, reference) => {
      const page =
        reference.blockIndex !== undefined && reference.blockIndex !== null
          ? reference.originalPage
          : reference.pageRangeStart
            ? reference.pageRangeStart
            : reference.originalPage;

      if (!refMap[reference.documentName]) {
        refMap[reference.documentName] = {
          [page]: [reference],
        };
      } else if (!refMap[reference.documentName][page]) {
        refMap[reference.documentName][page] = [reference];
      } else {
        refMap[reference.documentName][page].push(reference);
      }
      return refMap;
    }, {});
  }, [valueGroup.references]);
  const activeReference = useMemo(
    () =>
      valueGroup.references.find((r) =>
        activeReferenceId !== undefined ? r.id === activeReferenceId : r.blockIndex === activeBlockIndex
      ),
    [valueGroup.references, activeBlockIndex, activeReferenceId]
  );

  const referenceUpdated = useMemo(() => {
    if (activeReference) {
      const pageRange = pageRangeState[0];
      if (referenceTypeState[0] === "page") {
        return !!activeReference.pageRangeStart;
      } else if (pageRange) {
        return activeReference.pageRangeStart !== pageRange[0] || activeReference.pageRangeEnd !== pageRange[1];
      }
    }
    return false;
  }, [activeReference, referenceTypeState[0], pageRangeState[0]]);

  const handleEditReference = async () => {
    if (activeReference) {
      const pg = pageRangeState[0];
      await ffContext.updateReference({
        reference: activeReference,
        groupId: valueGroup.id,
        pageRangeStart: pg !== undefined ? pg[0] : undefined,
        pageRangeEnd: pg !== undefined ? pg[1] : undefined,
      });
    }
  };

  const deleteReference = async (referenceId: number) => {
    confirmCtx.confirm({
      title: "Delete Reference",
      prompt: <P variant="sm">Are you sure you want to delete this reference?</P>,
      callback: () => ffContext.removeReference(valueGroup.id, referenceId),
    });
  };

  useEffect(() => {
    if (activeReference) {
      const { pageRangeStart, pageRangeEnd } = activeReference;
      referenceTypeState[1](pageRangeStart ? "page_range" : "page");
      pageRangeState[1](pageRangeStart && pageRangeEnd ? [pageRangeStart, pageRangeEnd] : undefined);
    }
  }, [activeReference]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {Object.keys(referencesMap).map((documentName) => (
        <div key={documentName}>
          {showDocName && (
            <P
              variant="xsSemiBold"
              title={documentName}
              sx={{ mb: 1, display: "block", color: "grey.400", ...EllipsisSX }}>
              {documentName}
            </P>
          )}
          {Object.entries(referencesMap[documentName] ?? {}).map(([page, references]) => (
            <Card key={page} variant="outlined" sx={{ mb: 1 }}>
              <Box sx={{ p: 1, display: "flex", flexDirection: "column" }}>
                <div className="flex flex-col w-full">
                  {references.map((reference) => (
                    <FieldValueGroupReferenceLine
                      key={reference.id}
                      reference={reference}
                      activeReferenceId={activeReferenceId}
                      activeBlockIndex={activeBlockIndex}
                      page={page}
                      valueGroup={valueGroup}
                      isDisabled={cfContext.mutationInProgressHandle[0]}
                      dataPendo={dataPendo}
                      onClick={onClick}
                      onDelete={deleteReference}
                      canDelete={canDelete}
                      rangeEditorProps={{
                        pageRangeState,
                        referenceTypeState,
                        referenceUpdated,
                        onUpdate: handleEditReference,
                      }}
                    />
                  ))}
                </div>
                <p className="w-full mt-2 text-right text-gray-400 text-xxs">Pg {page}</p>
              </Box>
            </Card>
          ))}
        </div>
      ))}
    </Box>
  );
};
